import { PageRoute } from 'src/routes/routes-behind-login/page-route'
import {
  AnchorIcon,
  DashboardIcon,
  EmissonsIcon,
  FinanceReportIcon,
  RouteIcon,
  SpeedometerIcon,
  VesselFrontIcon,
} from '~components/icons'
import {
  ExactScopeName,
  useHasEntityRole,
  useHasScope,
} from '~hooks/permissions/use-has-scope'
import { useConstructLinkWithPool } from '~hooks/use-construct-link-with-pool'
import { useCharteringSubPages } from '~pages/pages-behind-login/chartering/position-list-page/hooks/use-chartering-sub-pages'
import { useEmissionsMenuLinks } from '~pages/pages-behind-login/emissions/emissions'
import { useFinancialSubPages } from '~pages/pages-behind-login/financials/use-financial-sub-pages'
import { useOperationsSubpages } from '~pages/pages-behind-login/operations/hooks/use-operations-sub-pages'
import { NavItem } from '../components/menu-items-renderer'
import { useShowLimitedExperience } from './use-show-limited-experience'

export function usePrimaryMenuLinks() {
  const topBarHeaders: NavItem[] = []
  const isPoolPartnerUser = useHasEntityRole('poolpartner', 'user')
  const isVesselOperations = useHasEntityRole('vessel', 'operations')
  const isVesselTechnical = useHasEntityRole('vessel', 'technical')
  const isVesselCrew = useHasEntityRole('vessel', 'crew')
  const isPoolAdmin = useHasScope(ExactScopeName.poolAdmin)
  const isDppLimited = useShowLimitedExperience()
  const isMtUser = useHasScope(ExactScopeName.maerskTankersUser)
  const hasPortInfoAccess = useHasScope(
    ExactScopeName.technicalManagement,
    ExactScopeName.portInfoRead,
    ExactScopeName.portInfoWrite,
  )
  const isPoolFilesWriter = useHasScope(ExactScopeName.poolFilesWriter)
  const dashboardLink = useConstructLinkWithPool('/dashboard/:pool')
  const emissionsLink = useConstructLinkWithPool('/emissions/:pool/*')
  const charteringSubPages = useCharteringSubPages()
  const financialsSubpages = useFinancialSubPages()
  const operationsSubpages = useOperationsSubpages()
  const emissionsSubpages = useEmissionsMenuLinks()

  if (
    !isDppLimited &&
    (isPoolPartnerUser || isPoolAdmin || isPoolFilesWriter)
  ) {
    topBarHeaders.push({
      title: 'dashboard',
      link: dashboardLink,
      isExternal: false,
      icon: <DashboardIcon />,
    })
  }

  if (!isDppLimited && (isPoolPartnerUser || isVesselTechnical || isMtUser)) {
    topBarHeaders.push({
      title: 'financials',
      link: 'financials',
      children: financialsSubpages,
      className: 'tour-step-nav-financials',
      icon: <FinanceReportIcon />,
    })
  }

  if (!isDppLimited && (isPoolPartnerUser || isMtUser)) {
    topBarHeaders.push({
      title: 'emissions',
      link: emissionsLink,
      children: emissionsSubpages,
      icon: <EmissonsIcon />,
    })
  }

  topBarHeaders.push({
    title: 'fleet',
    link: '/fleet/vessels',
    icon: <VesselFrontIcon />,
  })

  if (isMtUser) {
    topBarHeaders.push({
      title: 'chartering',
      link: PageRoute.CHARTERING,
      children: charteringSubPages,
      icon: <RouteIcon />,
    })
  }

  if (
    isMtUser ||
    isPoolPartnerUser ||
    isVesselCrew ||
    isVesselOperations ||
    isVesselTechnical
  ) {
    topBarHeaders.push({
      title: 'operations',
      link: PageRoute.OPERATIONS,
      children: operationsSubpages,
      icon: <SpeedometerIcon />,
    })
  }

  if (hasPortInfoAccess) {
    topBarHeaders.push({
      title: 'port info',
      link: PageRoute.PORT_INFO,
      icon: <AnchorIcon />,
    })
  }

  return topBarHeaders
}
